import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RotateCcw, Baby, Clock } from "lucide-react";
import LogToTrackerButton from "@/components/protocol/LogToTrackerButton";

const APGAR_CRITERIA = [
  {
    id: "appearance",
    letter: "A",
    label: "Appearance",
    subtitle: "Skin Color",
    scores: [
      { value: 0, desc: "Blue / pale all over" },
      { value: 1, desc: "Body pink, extremities blue" },
      { value: 2, desc: "Completely pink" },
    ],
  },
  {
    id: "pulse",
    letter: "P",
    label: "Pulse",
    subtitle: "Heart Rate",
    scores: [
      { value: 0, desc: "Absent" },
      { value: 1, desc: "< 100 bpm" },
      { value: 2, desc: ">= 100 bpm" },
    ],
  },
  {
    id: "grimace",
    letter: "G",
    label: "Grimace",
    subtitle: "Reflex Irritability",
    scores: [
      { value: 0, desc: "No response" },
      { value: 1, desc: "Grimace / weak cry" },
      { value: 2, desc: "Cry / active withdrawal" },
    ],
  },
  {
    id: "activity",
    letter: "A",
    label: "Activity",
    subtitle: "Muscle Tone",
    scores: [
      { value: 0, desc: "Limp / flaccid" },
      { value: 1, desc: "Some flexion" },
      { value: 2, desc: "Active motion" },
    ],
  },
  {
    id: "respiration",
    letter: "R",
    label: "Respiration",
    subtitle: "Breathing Effort",
    scores: [
      { value: 0, desc: "Absent" },
      { value: 1, desc: "Slow / irregular" },
      { value: 2, desc: "Good / crying" },
    ],
  },
];

function getInterpretation(score) {
  if (score >= 7) return { label: "Normal", color: "bg-green-500", textColor: "text-green-500", bgColor: "bg-green-500/10 border-green-500/30", guidance: "Routine post-delivery care" };
  if (score >= 4) return { label: "Moderately Depressed", color: "bg-amber-500", textColor: "text-amber-500", bgColor: "bg-amber-500/10 border-amber-500/30", guidance: "Stimulation, airway clearance, possible O2" };
  return { label: "Severely Depressed", color: "bg-red-500", textColor: "text-red-500", bgColor: "bg-red-500/10 border-red-500/30", guidance: "Immediate resuscitation required" };
}

export default function APGARCalculator() {
  const [scores1, setScores1] = useState({});
  const [scores5, setScores5] = useState({});
  const [activeMinute, setActiveMinute] = useState(1);
  const [open, setOpen] = useState(false);

  const scores = activeMinute === 1 ? scores1 : scores5;
  const setScores = activeMinute === 1 ? setScores1 : setScores5;

  const total1 = Object.values(scores1).reduce((s, v) => s + v, 0);
  const total5 = Object.values(scores5).reduce((s, v) => s + v, 0);
  const currentTotal = activeMinute === 1 ? total1 : total5;
  const answered1 = Object.keys(scores1).length;
  const answered5 = Object.keys(scores5).length;
  const currentAnswered = activeMinute === 1 ? answered1 : answered5;
  const interpretation = getInterpretation(currentTotal);

  const handleSelect = (criterionId, value) => {
    setScores(prev => {
      if (prev[criterionId] === value) {
        const next = { ...prev };
        delete next[criterionId];
        return next;
      }
      return { ...prev, [criterionId]: value };
    });
  };

  const handleReset = () => {
    if (activeMinute === 1) setScores1({});
    else setScores5({});
  };

  const handleResetAll = () => {
    setScores1({});
    setScores5({});
    setActiveMinute(1);
  };

  const hasBadge = answered1 > 0 || answered5 > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="apgar-trigger">
          <Baby className="w-4 h-4" />
          APGAR Calculator
          {hasBadge && (
            <span className="flex gap-1 ml-1">
              {answered1 > 0 && <Badge className={`${getInterpretation(total1).color} text-white`}>1m: {total1}</Badge>}
              {answered5 > 0 && <Badge className={`${getInterpretation(total5).color} text-white`}>5m: {total5}</Badge>}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Baby className="w-5 h-5" /> APGAR Score Calculator
          </DialogTitle>
          <p className="text-sm text-muted-foreground">Assess at 1 and 5 minutes after birth. Tap to select, tap again to deselect.</p>
        </DialogHeader>

        {/* Minute Toggle */}
        <div className="flex gap-2" data-testid="apgar-minute-toggle">
          <Button
            variant={activeMinute === 1 ? "default" : "outline"}
            size="sm"
            className="flex-1 gap-1.5"
            onClick={() => setActiveMinute(1)}
            data-testid="apgar-1min-tab"
          >
            <Clock className="w-3.5 h-3.5" /> 1 Minute
            {answered1 === 5 && <Badge variant="outline" className="ml-1 text-[10px]">{total1}/10</Badge>}
          </Button>
          <Button
            variant={activeMinute === 5 ? "default" : "outline"}
            size="sm"
            className="flex-1 gap-1.5"
            onClick={() => setActiveMinute(5)}
            data-testid="apgar-5min-tab"
          >
            <Clock className="w-3.5 h-3.5" /> 5 Minutes
            {answered5 === 5 && <Badge variant="outline" className="ml-1 text-[10px]">{total5}/10</Badge>}
          </Button>
        </div>

        {/* Score Summary */}
        <div className={`rounded-lg border p-4 ${currentAnswered > 0 ? interpretation.bgColor : 'bg-muted/30'}`} data-testid="apgar-score-summary">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-medium uppercase tracking-wide">{activeMinute}-Minute Score</span>
              <div className="flex items-baseline gap-1.5">
                <span className={`text-3xl font-bold font-mono ${currentAnswered > 0 ? interpretation.textColor : 'text-muted-foreground'}`}>{currentTotal}</span>
                <span className="text-sm text-muted-foreground">/ 10</span>
              </div>
            </div>
            <div className="text-right flex flex-col items-end gap-1">
              {currentAnswered > 0 && <Badge className={`${interpretation.color} text-white`}>{interpretation.label}</Badge>}
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={handleReset} className="h-6 text-xs px-2" data-testid="apgar-reset-current">
                  <RotateCcw className="w-3 h-3 mr-1" /> Reset
                </Button>
                {(answered1 > 0 || answered5 > 0) && (
                  <Button variant="ghost" size="sm" onClick={handleResetAll} className="h-6 text-xs px-2" data-testid="apgar-reset-all">
                    Reset All
                  </Button>
                )}
              </div>
            </div>
          </div>
          {currentAnswered > 0 && (
            <div className="mt-2 pt-2 border-t border-current/10">
              <span className="text-xs font-medium uppercase tracking-wide">Guidance</span>
              <p className={`font-semibold text-sm ${interpretation.textColor}`} data-testid="apgar-guidance">{interpretation.guidance}</p>
              {currentAnswered === 5 && (
                <LogToTrackerButton
                  className="w-full mt-2 h-8 text-xs"
                  testId={`apgar-log-${activeMinute}m`}
                  entry={{
                    type: "intervention",
                    name: `APGAR ${activeMinute}-min: ${currentTotal}/10`,
                    dose: interpretation.label,
                    notes: Object.entries(scores).map(([k, v]) => `${k.charAt(0).toUpperCase()}${k.slice(1)}: ${v}`).join(", "),
                  }}
                  successLabel={`APGAR ${activeMinute}m logged: ${currentTotal}/10`}
                  label="Log APGAR to Tracker"
                />
              )}
            </div>
          )}
        </div>

        {/* Both scores summary when both are filled */}
        {answered1 === 5 && answered5 === 5 && (
          <Card className="border-dashed" data-testid="apgar-comparison">
            <CardContent className="py-3">
              <div className="flex justify-around text-center">
                <div>
                  <span className="text-xs text-muted-foreground block">1-Minute</span>
                  <span className={`text-2xl font-bold font-mono ${getInterpretation(total1).textColor}`}>{total1}</span>
                  <Badge className={`${getInterpretation(total1).color} text-white block mt-1 text-[10px]`}>{getInterpretation(total1).label}</Badge>
                </div>
                <div className="w-px bg-border" />
                <div>
                  <span className="text-xs text-muted-foreground block">5-Minute</span>
                  <span className={`text-2xl font-bold font-mono ${getInterpretation(total5).textColor}`}>{total5}</span>
                  <Badge className={`${getInterpretation(total5).color} text-white block mt-1 text-[10px]`}>{getInterpretation(total5).label}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* APGAR Criteria */}
        <div className="space-y-3">
          {APGAR_CRITERIA.map((criterion) => (
            <div key={criterion.id} className="space-y-1.5" data-testid={`apgar-item-${criterion.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium">
                    <span className="text-primary font-bold">{criterion.letter}</span> — {criterion.label}
                  </span>
                  <span className="text-xs text-muted-foreground ml-2">({criterion.subtitle})</span>
                </div>
                {scores[criterion.id] !== undefined && (
                  <Badge variant="outline" className="font-mono text-xs">{scores[criterion.id]}</Badge>
                )}
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {criterion.scores.map(({ value, desc }) => (
                  <button
                    key={value}
                    onClick={() => handleSelect(criterion.id, value)}
                    className={`rounded-md px-2 py-2 text-xs font-medium transition-all text-left ${
                      scores[criterion.id] === value
                        ? value === 0 ? 'bg-red-500/20 text-red-400 ring-2 ring-red-500/50'
                        : value === 1 ? 'bg-amber-500/20 text-amber-400 ring-2 ring-amber-500/50'
                        : 'bg-green-500/20 text-green-400 ring-2 ring-green-500/50'
                        : 'bg-secondary/40 hover:bg-secondary/80 text-foreground'
                    }`}
                    data-testid={`apgar-${criterion.id}-${value}`}
                  >
                    <span className="font-bold block">{value}</span>
                    <span className="opacity-80 leading-tight block">{desc}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
